import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import ReactTooltip from "react-tooltip";

import {
  getSponsorList,
  postSponsorCreate,
  postSponsorDelete,
  postSponsorUpdate,
} from "../../../../apis";
import UploadIcon from "../../../../assets/icons/UploadIcon";
import Title from "../../../../components/common/Title";
import { isValidHttpsWebsite } from "../../../../functions";
import CreateSponsor from "./CreateSponsor";
import Information from "../../../../assets/images/information.png";

const SPONSORS_TITLE = ["Sponsored by", "Powered By", "Co-Powered By"];
const SPONSORS_TITLE_SET = new Set([
  "Sponsored by",
  "Powered By",
  "Co-Powered By",
]);

const SPONSOR_POSITION_MAP = {};

const EventSponsor = () => {
  const { eventId } = useParams();

  const [showCreateSponsor, setShowCreateSponsor] = useState(false);
  const [sponsorList, setSponsorList] = useState([]);

  const onSave = async (id, sponsorValue, positionValue, image, webLink) => {
    try {
      if (webLink && !isValidHttpsWebsite(webLink)) {
        window.Toast.fire({
          icon: "error",
          title: "Please add a proper redirect url",
        });
        return false;
      }
      if (!(sponsorValue && positionValue && image)) {
        window.Toast.fire({
          icon: "error",
          title: "Please fill all the fields",
        });
        return false;
      }

      if (
        SPONSOR_POSITION_MAP[positionValue] &&
        SPONSOR_POSITION_MAP[positionValue].value &&
        SPONSOR_POSITION_MAP[positionValue].value.trim() !==
          sponsorValue.trim() &&
        SPONSOR_POSITION_MAP[positionValue].index.size
      ) {
        if (SPONSOR_POSITION_MAP[positionValue].index.size < 2) {
          if (SPONSOR_POSITION_MAP[positionValue].index.has(id)) {
          } else {
            window.Toast.fire({
              icon: "error",
              title: "Only one type of sponsors are allowed on one position",
            });
            callSponsorList();
            return false;
          }
        } else {
          window.Toast.fire({
            icon: "error",
            title: "Only one type of sponsors are allowed on one position",
          });
          callSponsorList();
          return false;
        }
      }
      if (id) {
        const payload = {
          logo_url: image,
          position: positionValue,
          type: sponsorValue,
          id: id,
          link: webLink,
        };
        await postSponsorUpdate(payload);
        window.Toast.fire({
          icon: "success",
          title: "Sponsor updated successfully",
        });
        if (!SPONSORS_TITLE_SET.has(sponsorValue)) {
          SPONSORS_TITLE.push(sponsorValue);
          SPONSORS_TITLE_SET.add(sponsorValue);
        }
        if (SPONSOR_POSITION_MAP[positionValue])
          SPONSOR_POSITION_MAP[positionValue].value = sponsorValue;
        else SPONSOR_POSITION_MAP[positionValue] = { value: sponsorValue };
        const index = sponsorList.findIndex((el) => el.id === id);
        const sponsorListClone = structuredClone(sponsorList);
        sponsorListClone[index] = {
          imageUrl: image,
          position: positionValue,
          sponsor: sponsorValue,
          id: id,
          websiteLink: webLink,
        };
        setSponsorList(sponsorListClone);
        setShowCreateSponsor(false);
      } else {
        const payload = {
          event_id: eventId,
          logo_url: image,
          position: positionValue,
          type: sponsorValue,
          link: webLink,
        };
        const { data } = await postSponsorCreate(payload);
        window.Toast.fire({
          icon: "success",
          title: "Sponsor added successfully",
        });
        if (!SPONSORS_TITLE_SET.has(sponsorValue)) {
          SPONSORS_TITLE.push(sponsorValue);
          SPONSORS_TITLE_SET.add(sponsorValue);
        }
        SPONSOR_POSITION_MAP[positionValue] = { value: sponsorValue };
        if (SPONSOR_POSITION_MAP[positionValue].index)
          SPONSOR_POSITION_MAP[positionValue].index.add(data.id);
        else SPONSOR_POSITION_MAP[positionValue].index = new Set([data.id]);
        setSponsorList((prev) => [
          ...prev,
          {
            imageUrl: image,
            position: positionValue,
            sponsor: sponsorValue,
            id: data.id,
            websiteLink: webLink,
          },
        ]);
        setShowCreateSponsor(false);
      }
      return true;
    } catch (error) {
      console.error(error);
      window.Toast.fire({ icon: "error", title: "Something went wrong" });
      callSponsorList();
      return false;
    }
  };

  const callSponsorList = async () => {
    try {
      const { data } = await getSponsorList(eventId);

      setSponsorList(
        data.map((el) => {
          if (!SPONSORS_TITLE_SET.has(el.type)) {
            SPONSORS_TITLE.push(el.type);
            SPONSORS_TITLE_SET.add(el.type);
          }
          SPONSOR_POSITION_MAP[el.position] = { value: el.type };
          if (SPONSOR_POSITION_MAP[el.position].index)
            SPONSOR_POSITION_MAP[el.position].index.add(el.id);
          else SPONSOR_POSITION_MAP[el.position].index = new Set([el.id]);
          return {
            imageUrl: el.logo_url,
            position: el.position,
            sponsor: el.type,
            id: el.id,
            websiteLink: el.link,
          };
        })
      );
    } catch (error) {
      console.error(error);
      window.Toast.fire({
        icon: "error",
        title: "Unable to fetch sponsor list",
      });
    }
  };

  const onRemove = async (id) => {
    if (id) {
      Swal.fire({
        title: "Delete Sponsor",
        text: "Are you sure you want to delete this sponsor ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
        showLoaderOnConfirm: true,
        preConfirm: () => {
          return postSponsorDelete({ sponsorId: id, event_id: eventId })
            .then((response) => {
              window.Toast.fire({
                icon: "success",
                title: "Your sponsor has been deleted successfully",
              });

              setSponsorList((prev) =>
                prev.filter((el) => {
                  if (el.id === id) {
                    SPONSOR_POSITION_MAP[el.position].index.delete(id);
                  }
                  return el.id != id;
                })
              );
            })
            .catch((err) => {
              console.error(err);
              Swal.fire(
                "Error!",
                err.response?.data?.message ?? "Something went wrong",
                "error"
              );
            });
        },
      });
    } else {
      window.Toast.fire({
        icon: "error",
        title: "Something went wrong",
      });
    }
  };

  useEffect(() => {
    callSponsorList();
  }, []);

  return (
    <div>
      <Title
        className="pb-4 mb-6 border-b border-secondary-600 mt-4"
        text={
          <>
            <div>Event Sponsors</div>{" "}
            <span
              className="pl-3 cursor-pointer relative"
              data-tip
              data-for="eventSponsorTip"
            >
              <img className="cursor-pointer" src={Information} />
              <ReactTooltip
                id="eventSponsorTip"
                multiline={true}
                type="dark"
                className="z-[100] !opacity-100"
              >
                <div>
                  <h3 className="text-xl font-medium mb-1">Feature Overview</h3>
                  <ul className="list-disc px-4 mb-2 text-sm">
                    <li>
                      Give your sponsors the recognition they deserve with our
                      customizable sponsor showcase.
                    </li>
                  </ul>
                  <div className="w-full mt-2 mb-2 h-[1px] bg-[#555555]"></div>
                  <h3 className="text-xl font-medium mb-1">How to use</h3>
                  <ol className="list-decimal px-4 mb-2 text-sm">
                    <li>
                      Click 'Upload sponsor logo' to upload the sponsor logo.
                      <li>Select a sponsor type.</li>
                      <li>Choose a display position.</li>
                      <li>Add a hyperlink (if needed).</li>
                      <li>Click on tick mark.</li>
                    </li>
                  </ol>
                  <span className="text-xs">
                    {/* "Click 'Upload Sponsor Logo' to upload the sponsor logo,
                    select a sponsor type, choose a display position, and add a
                    hyperlink and click on save. The sponsor will appear in the gallery based on
                    the selected position." */}
                  </span>

                  <video
                    muted
                    autoPlay
                    loop
                    playsinline
                    src={"https://img2.pixnif.com/assets/videos/sponsor.mov"}
                    height={400}
                    width={400}
                  />
                </div>
              </ReactTooltip>
            </span>
          </>
        }
      />

      {sponsorList.map((el) => (
        <div key={el.id} className="mt-4">
          <CreateSponsor
            onSave={onSave}
            sponsor={el.sponsor}
            position={el.position}
            imageUrl={el.imageUrl}
            onRemove={onRemove}
            id={el.id}
            SPONSORS_TITLE={SPONSORS_TITLE}
            websiteLink={el.websiteLink}
          />{" "}
        </div>
      ))}

      {showCreateSponsor ? (
        <div className="my-8">
          <CreateSponsor
            onSave={onSave}
            onRemove={onRemove}
            SPONSORS_TITLE={SPONSORS_TITLE}
            onCancel={() => setShowCreateSponsor(false)}
            openUploadPopUp={showCreateSponsor}
          />
        </div>
      ) : (
        <></>
      )}

      <button
        role="button"
        title="Add sponsor"
        onClick={() => {
          if (showCreateSponsor) {
            window.Toast.fire({
              icon: "error",
              title: "Editing of other sponsor is going on",
            });
            return;
          }
          setShowCreateSponsor(true);
        }}
        className={`font-medium mt-4 cursor-pointer !bg-dark-gray flex gap-4 items-center rounded-xl shadow-card  px-3 py-2.5 w-fit`}
      >
        <UploadIcon color="#00b7b8" /> Upload sponsor logo
      </button>
    </div>
  );
};

export default EventSponsor;
